import { 
  ChallengeTableResponse, 
  ChallengeTableInspectionsProgramsVersionsItem,
  ChallengeTableProgramVersionItem 
} from '../api/types/dtos/challenge/ChallengeDto';

// Status enum for challenge items
export enum ChallengeItemStatus {
  PENDIENTE = 'PENDIENTE',
  IN_PROCESS = 'IN_PROCESS', 
  APPROVED = 'APPROVED'
}

// Analytics data structures
export interface LevelAnalytics {
  levelId: string;
  levelName: string;
  approved: number;
  inProcess: number;
  pending: number;
  total: number;
}

export interface ChallengeAnalytics {
  challengeId: string;
  challengeName: string;
  cpNumber: string;
  inProcess: number;
  approved: number;
  total: number;
}

export interface StatusDistribution {
  approved: number;
  inProcess: number;
  total: number;
}

export interface FarmCompletionDistribution {
  challengesCompleted: number;
  farmCount: number;
}

export interface AnalyticsData {
  levelAnalytics: LevelAnalytics[];
  challengeAnalytics: ChallengeAnalytics[];
  statusDistribution: StatusDistribution;
  farmCompletionDistribution: FarmCompletionDistribution[];
}

/**
 * Service class to process challenge table data and generate analytics
 * Optimized for performance with large datasets (200 orgs × 40 challenges)
 */
export class ChallengeAnalyticsService {
  
  /**
   * Determines the status of a challenge item based on isActive and isApproved flags
   * Logic: Inactive + Not Approved = PENDIENTE
   *        Active + Not Approved = IN_PROCESS  
   *        Active + Approved = APPROVED
   */
  private static getItemStatus(item: ChallengeTableInspectionsProgramsVersionsItem): ChallengeItemStatus {
    if (!item.isActive && !item.isApproved) {
      return ChallengeItemStatus.PENDIENTE;
    }
    if (item.isActive && !item.isApproved) {
      return ChallengeItemStatus.IN_PROCESS;
    }
    if (item.isActive && item.isApproved) {
      return ChallengeItemStatus.APPROVED;
    }
    // Fallback case (inactive + approved shouldn't happen, but treat as pending)
    return ChallengeItemStatus.PENDIENTE;
  }

  /**
   * Processes challenge table data to generate comprehensive analytics
   * Uses efficient Map-based aggregation for O(n) performance
   */
  public static processAnalytics(data: ChallengeTableResponse): AnalyticsData {
    if (!data?.data?.challenges || !data?.data?.programVersionItems) {
          return {
      levelAnalytics: [],
      challengeAnalytics: [],
      statusDistribution: { approved: 0, inProcess: 0, total: 0 },
      farmCompletionDistribution: []
    };
    }

    const { challenges, programVersionItems } = data.data;

    // Get current year in UTC
    const currentYear = new Date().getUTCFullYear();

    // Filter challenges to only include items with endDate in the current year
    const filteredChallenges = challenges.map(challenge => ({
      ...challenge,
      inspectionsProgramsVersionsItems: challenge.inspectionsProgramsVersionsItems.filter(item => {
        if (!item.endDate) return false;
        const endDate = new Date(item.endDate);
        return endDate.getUTCFullYear() === currentYear;
      })
    })).filter(challenge => challenge.inspectionsProgramsVersionsItems.length > 0);

    // Create lookup maps for efficient processing
    const programItemMap = new Map<string, ChallengeTableProgramVersionItem>();
    programVersionItems.forEach(item => {
      programItemMap.set(item.id, item);
    });

    // Aggregation maps for efficient counting
    const levelStatsMap = new Map<string, { 
      levelName: string; 
      approved: number; 
      inProcess: number; 
      pending: number; 
    }>();

    const challengeStatsMap = new Map<string, {
      challengeName: string;
      cpNumber: string;
      inProcess: number;
      approved: number;
    }>();

    let totalApproved = 0;
    let totalInProcess = 0;
    let totalPending = 0;

    // Single pass through all challenge items for O(n) performance
    challenges.forEach(challenge => {
      challenge.inspectionsProgramsVersionsItems.forEach(item => {
        const programItem = programItemMap.get(item.programVersionItemId);
        if (!programItem) return;
        const status = this.getItemStatus(item);
        const levelId = programItem.programLevelId;
        const levelName = programItem.programLevelName;
        const challengeName = programItem.name;
        const cpNumber = programItem.cpNumber;

        // For APPROVED, only count if endDate is in current year
        let countApproved = false;
        if (status === ChallengeItemStatus.APPROVED) {
          if (item.endDate) {
            const endDate = new Date(item.endDate);
            if (endDate.getUTCFullYear() === currentYear) {
              countApproved = true;
            }
          }
        }

        // Update level statistics
        if (!levelStatsMap.has(levelId)) {
          levelStatsMap.set(levelId, {
            levelName,
            approved: 0,
            inProcess: 0,
            pending: 0
          });
        }
        const levelStats = levelStatsMap.get(levelId)!;
        switch (status) {
          case ChallengeItemStatus.APPROVED:
            if (countApproved) {
              levelStats.approved++;
              totalApproved++;
            }
            break;
          case ChallengeItemStatus.IN_PROCESS:
            levelStats.inProcess++;
            totalInProcess++;
            break;
          case ChallengeItemStatus.PENDIENTE:
            levelStats.pending++;
            totalPending++;
            break;
        }

        // Update challenge statistics (only count approved and in-process)
        if (status === ChallengeItemStatus.APPROVED || status === ChallengeItemStatus.IN_PROCESS) {
          if (!challengeStatsMap.has(item.programVersionItemId)) {
            challengeStatsMap.set(item.programVersionItemId, {
              challengeName,
              cpNumber,
              inProcess: 0,
              approved: 0
            });
          }
          const challengeStats = challengeStatsMap.get(item.programVersionItemId)!;
          if (status === ChallengeItemStatus.APPROVED) {
            if (countApproved) {
              challengeStats.approved++;
            }
          } else {
            challengeStats.inProcess++;
          }
        }
      });
    });

    // Convert maps to arrays and sort for consistent output
    const levelAnalytics: LevelAnalytics[] = Array.from(levelStatsMap.entries())
      .map(([levelId, stats]) => ({
        levelId,
        levelName: stats.levelName,
        approved: stats.approved,
        inProcess: stats.inProcess,
        pending: stats.pending,
        total: stats.approved + stats.inProcess + stats.pending
      }))
      .sort((a, b) => a.levelName.localeCompare(b.levelName));

    // First, collect all challenges that have data
    const challengesWithData = Array.from(challengeStatsMap.entries())
      .map(([challengeId, stats]) => ({
        challengeId,
        challengeName: stats.challengeName,
        cpNumber: stats.cpNumber,
        inProcess: stats.inProcess,
        approved: stats.approved,
        total: stats.inProcess + stats.approved
      }));

    // Then, add all challenges that don't have data (zero values)
    const challengesWithDataIds = new Set(challengesWithData.map(c => c.challengeId));
    const allChallenges = [...challengesWithData];
    
    programVersionItems.forEach(pvi => {
      if (!challengesWithDataIds.has(pvi.id)) {
        allChallenges.push({
          challengeId: pvi.id,
          challengeName: pvi.name,
          cpNumber: pvi.cpNumber,
          inProcess: 0,
          approved: 0,
          total: 0
        });
      }
    });

    const challengeAnalytics: ChallengeAnalytics[] = allChallenges
      .sort((a, b) => {
        // Sort by cpNumber alphabetically, handling numeric parts properly
        const aParts = a.cpNumber.split('.').map(part => isNaN(Number(part)) ? part : Number(part));
        const bParts = b.cpNumber.split('.').map(part => isNaN(Number(part)) ? part : Number(part));
        
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aPart = aParts[i] || 0;
          const bPart = bParts[i] || 0;
          
          if (aPart < bPart) return -1;
          if (aPart > bPart) return 1;
        }
        
        return 0;
      });

    const statusDistribution: StatusDistribution = {
      approved: totalApproved,
      inProcess: totalInProcess,
      total: totalApproved + totalInProcess
    };

    // Calculate farm completion distribution
    const farmCompletionMap = new Map<string, number>();
    
    // Count approved challenges per farm
    challenges.forEach(challenge => {
      const farmId = challenge.inspectedOrgId;
      if (!farmCompletionMap.has(farmId)) {
        farmCompletionMap.set(farmId, 0);
      }
      // Count approved challenges for this farm (only if endDate is in current year)
      challenge.inspectionsProgramsVersionsItems.forEach(item => {
        if (this.getItemStatus(item) === ChallengeItemStatus.APPROVED) {
          if (item.endDate) {
            const endDate = new Date(item.endDate);
            if (endDate.getUTCFullYear() === currentYear) {
              farmCompletionMap.set(farmId, farmCompletionMap.get(farmId)! + 1);
            }
          }
        }
      });
    });

    // Create distribution of farms by number of completed challenges
    const distributionMap = new Map<number, number>();
    farmCompletionMap.forEach(completedCount => {
      distributionMap.set(completedCount, (distributionMap.get(completedCount) || 0) + 1);
    });

    // Convert to array and fill gaps (0 farms with X completed challenges)
    const maxCompleted = Math.max(...Array.from(farmCompletionMap.values()), 0);
    const farmCompletionDistribution: FarmCompletionDistribution[] = [];
    
    for (let i = 0; i <= maxCompleted; i++) {
      farmCompletionDistribution.push({
        challengesCompleted: i,
        farmCount: distributionMap.get(i) || 0
      });
    }

    return {
      levelAnalytics,
      challengeAnalytics,
      statusDistribution,
      farmCompletionDistribution
    };
  }

  /**
   * Gets status color based on status type
   */
  public static getStatusColor(status: ChallengeItemStatus): string {
    switch (status) {
      case ChallengeItemStatus.APPROVED:
        return '#6fbf73'; // Green
      case ChallengeItemStatus.IN_PROCESS:
        return '#FFC107'; // Yellow
      case ChallengeItemStatus.PENDIENTE:
        return 'rgba(0, 0, 0, 0.3)'; // Light/transparent
      default:
        return '#9E9E9E'; // Gray fallback
    }
  }
} 